'use client'

import { Close } from '@mui/icons-material'
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardMedia,
	Checkbox,
	FormControlLabel,
	IconButton,
	Link as MuiLink,
	Stack,
	Typography
} from '@mui/material'
import { Logger, Field } from '@wolfdanepug/common-react'
import { noop } from 'lodash'
import { useRef, useState } from 'react'
import { NavLink } from 'react-router'

export interface PlanItemProps {
	name: string
	title?: string
	description?: string
	image?: string
	selected?: boolean
	allowWeightSelect?: boolean
	availableWeights?: any[]
	currentWeight?: number
	quoteWeight?: number
	actionLabel?: string
	updatePrice?: (weight: number) => Awaited<any>
	onChange?: (selected: boolean) => void
	onWeightChange?: (weight: number) => void
}

export default function PlanItem({
	name,
	title,
	description,
	image,
	selected = false,
	allowWeightSelect = false,
	availableWeights = [],
	currentWeight,
	quoteWeight,
	actionLabel = 'Add',
	onChange = noop,
	onWeightChange = noop,
	updatePrice = noop
}: PlanItemProps) {
	Logger.log('[PlanItem]', quoteWeight, currentWeight)
	const { current: currentWeightRef } = useRef(quoteWeight || currentWeight)
	const [weightSelectActive, setWeightSelectActive] = useState<boolean>(Boolean(quoteWeight))
	const cardStyles = {
		borderRadius: 3,
		height: '100%',
		// maxWidth: 200,
		display: 'flex',
		flexDirection: 'column',
		'&.MuiPaper-root': {
			boxShadow: selected
				? '0px 2px 1px -1px rgba(90, 244, 5, 0.2), 0px 1px 1px 0px rgba(90, 244, 5, 0.14), 0px 1px 3px 0px rgba(90, 244, 5, 0.12);'
				: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);'
		}
	}

	const handleClose = () => {
		setWeightSelectActive(false)
		currentWeightRef && onWeightChange(currentWeightRef)
	}

	const handleWeightChange = async (weight: number) => {
		onWeightChange(weight)
		const newPrice = await updatePrice(weight)
		Logger.log('[PlanItem] handleWeightChange', newPrice)
	}

	Logger.log('[PlanItem]', name, availableWeights)

	return (
		<Box sx={styles.root}>
			<Card sx={cardStyles}>
				<CardMedia component={'img'} image={image} alt={title} sx={styles.media} />
				<CardContent sx={styles.content}>
					<Box sx={{ display: 'flex', flex: 1 }}>
						<Typography variant="h3" sx={styles.title}>
							{name}
						</Typography>
						<Typography variant="body2">{description}</Typography>
					</Box>
					<Box>
						<MuiLink color="primary" component={NavLink} to="/recipes/chicken-and-grains">
							See Details
						</MuiLink>
					</Box>
					<Box>
						<Typography variant="body2">Current Portion Size {currentWeight} oz.</Typography>
					</Box>
				</CardContent>
				<CardActions sx={styles.actions}>
					<Stack spacing={2} direction={'row'} flex={1}>
						<Box sx={{ flex: 1 }}>
							<FormControlLabel
								label={actionLabel}
								control={
									<Checkbox
										name={name}
										checked={selected}
										onChange={({ target }: any) => onChange(!!target.checked)}
									/>
								}
							/>
						</Box>
						{allowWeightSelect && (
							<Stack>
								{weightSelectActive ? (
									<Stack direction={'row'} alignItems={'center'} gap={1}>
										<Box sx={{ flex: 1 }}>
											<Field
												name="weight"
												label="Weight"
												type="select"
												fullWidth
												size="small"
												defaultValue={currentWeightRef}
												options={availableWeights.map((weight: any) => ({
													label: `${weight} oz.`,
													value: weight
												}))}
												onChange={({ target }: any) => handleWeightChange(target.value)}
											/>
										</Box>
										<IconButton onClick={handleClose} sx={{ alignSelf: 'flex-start' }}>
											<Close />
										</IconButton>
									</Stack>
								) : (
									<Button variant="contained" onClick={() => setWeightSelectActive(true)}>
										Change portion size
									</Button>
								)}
							</Stack>
						)}
					</Stack>
				</CardActions>
			</Card>
		</Box>
	)
}

const styles: any = {
	root: {
		position: 'relative',
		pt: '130px',
		flex: 1
	},
	media: {
		position: 'absolute',
		transform: 'translate(-50%, -50%)',
		left: '50%',
		maxWidth: 200
	},
	actions: {
		pl: 2,
		pr: 2
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		pt: '130px',
		flex: 1,
		minHeight: 200
		// maxWidth: 200
	},
	title: {
		fontSize: '1.2rem',
		mb: 2
	}
}
